import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Pagination, ListGroup, Modal, Offcanvas } from 'react-bootstrap';
import { Routes, Route, useNavigate, Link, useLocation } from 'react-router-dom';
import './App.css';
import LoginModal from './components/LoginModal';
import EditorPage from './pages/EditorPage';
import PostDetailPage from './pages/PostDetailPage';
import PostItem from './components/PostItem';
import { clearAuthCookies, getValidAccessTokenOrRefresh, hasValidAuthToken } from './utils/AuthTokenUtil';
import { ArchiveDay, deletePost, getActiveNotices, getArchiveDays, getPopularPosts, getPostPage, getPostsByDate, getPostsByTag, searchPosts } from './services/PostService';
import { Category, getCategories } from './services/CategoryService';
import { getCurrentWeather, WeatherRegion, WeatherResponse } from './services/WeatherService';
import { getPopularTags, PopularTag } from './services/TagService';
import { CalendarHoliday, getCalendarHolidays } from './services/CalendarService';
import { trackVisitor, VisitorStats } from './services/VisitorService';
import { CurrentUser, getCurrentUser } from './services/AuthService';

const PAGE_SIZE = 5;
const PAGE_GROUP_SIZE = 5;
const SEASON_BANNERS = [
  { image: '/images/season-banner-winter.png', label: '겨울 풍경 배너' },
  { image: '/images/season-banner-spring.png', label: '봄 풍경 배너' },
  { image: '/images/season-banner-summer.png', label: '여름 풍경 배너' },
  { image: '/images/season-banner-autumn.png', label: '가을 풍경 배너' },
];
const WEATHER_REGIONS: WeatherRegion[] = [
  { key: 'seoul', name: '서울', nx: 60, ny: 127 },
  { key: 'incheon', name: '인천', nx: 55, ny: 124 },
  { key: 'gimpo', name: '김포', nx: 55, ny: 124 },
  { key: 'jeonju', name: '전주', nx: 63, ny: 89 },
  { key: 'gangneung', name: '강릉', nx: 92, ny: 131 },
  { key: 'daegu', name: '대구', nx: 89, ny: 90 },
  { key: 'busan', name: '부산', nx: 98, ny: 76 },
  { key: 'jeju', name: '제주', nx: 52, ny: 38 },
];
const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

const formatDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMonthLabel = (date: Date) => `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}`;

const getCurrentSeasonBanner = () => {
  const month = new Date().getMonth() + 1;
  if (month >= 3 && month <= 5) {
    return SEASON_BANNERS[1];
  }
  if (month >= 6 && month <= 8) {
    return SEASON_BANNERS[2];
  }
  if (month >= 9 && month <= 11) {
    return SEASON_BANNERS[3];
  }
  return SEASON_BANNERS[0];
};

const buildCalendarCells = (monthDate: Date) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const cells: Array<Date | null> = [];

  for (let index = 0; index < firstDay.getDay(); index += 1) {
    cells.push(null);
  }

  for (let day = 1; day <= lastDay.getDate(); day += 1) {
    cells.push(new Date(year, month, day));
  }

  while (cells.length % 7 !== 0) {
    cells.push(null);
  }

  return cells;
};

const MainLayout: React.FC<{ isAuthenticated: boolean; currentUser: CurrentUser | null; handleLogout: () => void; setShowLogin: (s: boolean) => void; validateAuthentication: () => Promise<boolean> }> = ({ isAuthenticated, currentUser, handleLogout, setShowLogin, validateAuthentication }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isWriting = location.pathname.startsWith('/write');
  const [posts, setPosts] = useState<any[]>([]);
  const [activeNotices, setActiveNotices] = useState<any[]>([]);
  const [selectedNotice, setSelectedNotice] = useState<any | null>(null);
  const [postToDelete, setPostToDelete] = useState<any | null>(null);
  const [postDeleting, setPostDeleting] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [popularTags, setPopularTags] = useState<PopularTag[]>([]);
  const [popularPosts, setPopularPosts] = useState<any[]>([]);
  const [archiveDays, setArchiveDays] = useState<ArchiveDay[]>([]);
  const [calendarHolidays, setCalendarHolidays] = useState<CalendarHoliday[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [tagLoading, setTagLoading] = useState(true);
  const [popularPostLoading, setPopularPostLoading] = useState(true);
  const [archiveLoading, setArchiveLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>();
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [archiveMonth, setArchiveMonth] = useState(() => new Date());
  const [weather, setWeather] = useState<WeatherResponse | null>(null);
  const [weatherLoading, setWeatherLoading] = useState(true);
  const [weatherError, setWeatherError] = useState('');
  const [visitorStats, setVisitorStats] = useState<VisitorStats | null>(null);
  const [visitorLoading, setVisitorLoading] = useState(true);
  const [selectedWeatherRegion, setSelectedWeatherRegion] = useState<WeatherRegion>(WEATHER_REGIONS[0]);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);
  const isFiltered = searchKeyword.trim().length > 0 || selectedCategoryId !== undefined;
  const restorePage = Number.isInteger(location.state?.restorePage) ? location.state.restorePage : 0;
  const getListToken = useCallback(async () => (isAuthenticated ? await getValidAccessTokenOrRefresh() : null), [isAuthenticated]);

  const fetchInitialData = useCallback(async (page: number = 0) => {
    setLoading(true);
    setCategoryLoading(true);
    setTagLoading(true);
    setPopularPostLoading(true);
    try {
      const token = await getListToken();
      const [postPage, categoryData, tagData, popularPostData] = await Promise.all([
        getPostPage(page, PAGE_SIZE, token),
        getCategories(token),
        getPopularTags(20, token),
        getPopularPosts(5, token),
      ]);
      setPosts(postPage.content);
      setCurrentPage(postPage.number);
      setTotalPages(postPage.totalPages);
      setCategories(categoryData);
      setPopularTags(tagData);
      setPopularPosts(popularPostData);
    } catch (err) {
      console.error('Failed to fetch data', err);
    } finally {
      setLoading(false);
      setCategoryLoading(false);
      setTagLoading(false);
      setPopularPostLoading(false);
    }
  }, [getListToken]);

  const fetchActiveNotices = useCallback(async () => {
    try {
      setActiveNotices(await getActiveNotices(await getListToken()));
    } catch (err) {
      console.error('Failed to fetch active notices', err);
      setActiveNotices([]);
    }
  }, [getListToken]);

  const fetchArchiveDays = useCallback(async (monthDate: Date) => {
    setArchiveLoading(true);

    try {
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth() + 1;
      const archiveData = await getArchiveDays(year, month, await getListToken());
      setArchiveDays(archiveData);
    } catch (err) {
      console.error('Failed to fetch archive days', err);
      setArchiveDays([]);
    }

    try {
      const year = monthDate.getFullYear();
      const month = monthDate.getMonth() + 1;
      setCalendarHolidays(await getCalendarHolidays(year, month));
    } catch (err) {
      console.error('Failed to fetch calendar holidays', err);
      setCalendarHolidays([]);
    } finally {
      setArchiveLoading(false);
    }
  }, [getListToken]);

  const fetchWeather = useCallback(async (region: WeatherRegion) => {
    setWeatherLoading(true);
    setWeatherError('');

    try {
      setWeather(await getCurrentWeather(region));
    } catch (err) {
      console.error('Failed to fetch weather', err);
      setWeatherError('날씨 정보를 불러오지 못했습니다.');
    } finally {
      setWeatherLoading(false);
    }
  }, []);

  const fetchVisitorStats = useCallback(async () => {
    setVisitorLoading(true);

    try {
      setVisitorStats(await trackVisitor());
    } catch (err) {
      console.error('Failed to track visitor', err);
      setVisitorStats(null);
    } finally {
      setVisitorLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInitialData(0);
    fetchActiveNotices();
  }, [fetchInitialData, fetchActiveNotices]);

  useEffect(() => {
    if (!mobileSearchOpen) {
      return;
    }

    const focusTimer = window.setTimeout(() => {
      mobileSearchInputRef.current?.focus();
    }, 80);

    return () => window.clearTimeout(focusTimer);
  }, [mobileSearchOpen]);

  useEffect(() => {
    fetchWeather(selectedWeatherRegion);
  }, [fetchWeather, selectedWeatherRegion]);

  useEffect(() => {
    fetchVisitorStats();
  }, [fetchVisitorStats]);

  useEffect(() => {
    fetchArchiveDays(archiveMonth);
  }, [archiveMonth, fetchArchiveDays]);

  useEffect(() => {
    if (location.pathname !== '/' || !location.state?.refreshPosts) {
      return;
    }

    const currentMonth = new Date();
    setSearchKeyword('');
    setSelectedCategoryId(undefined);
    fetchInitialData(restorePage);
    fetchActiveNotices();
    setArchiveMonth(currentMonth);
    fetchArchiveDays(currentMonth);
    navigate('/', { replace: true, state: null });
  }, [location.pathname, location.state, navigate, restorePage, fetchInitialData, fetchActiveNotices, fetchArchiveDays]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await searchPosts(searchKeyword, selectedCategoryId, await getListToken());
      setPosts(data);
      setCurrentPage(0);
      setTotalPages(data.length > 0 ? 1 : 0);
      if (location.pathname !== '/') {
        navigate('/');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Search failed', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTagClick = async (tagName: string) => {
    setSearchKeyword(tagName);
    setSelectedCategoryId(undefined);
    setLoading(true);

    try {
      const data = await getPostsByTag(tagName, await getListToken());
      setPosts(data);
      setCurrentPage(0);
      setTotalPages(data.length > 0 ? 1 : 0);
      if (location.pathname !== '/') {
        navigate('/');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Tag search failed', err);
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveDateClick = async (date: string) => {
    setSearchKeyword(date);
    setSelectedCategoryId(undefined);
    setLoading(true);

    try {
      const data = await getPostsByDate(date, await getListToken());
      setPosts(data);
      setCurrentPage(0);
      setTotalPages(data.length > 0 ? 1 : 0);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Archive search failed', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = async (categoryId: number) => {
    const newId = categoryId;
    setSelectedCategoryId(newId);
    setSearchKeyword('');
    if (newId && categories.some((category) => category.parentId === categoryId)) {
      setExpandedCategoryIds((current) => {
        const next = new Set(current);
        next.add(categoryId);
        return next;
      });
    }
    setLoading(true);
    try {
      const data = await searchPosts('', newId, await getListToken());
      setPosts(data);
      setCurrentPage(0);
      setTotalPages(data.length > 0 ? 1 : 0);
      if (location.pathname !== '/') {
        navigate('/');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Category search failed', err);
    } finally {
      setLoading(false);
    }
  };

  const handleShowAllPosts = async () => {
    setSearchKeyword('');
    setSelectedCategoryId(undefined);
    setShowMobileMenu(false);
    await fetchInitialData(0);
    if (location.pathname !== '/') {
      navigate('/');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleCategoryExpanded = (categoryId: number) => {
    setExpandedCategoryIds((current) => {
      const next = new Set(current);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const handleWriteClick = async () => {
    if (await validateAuthentication()) {
      navigate('/write');
      return;
    }

    setShowLogin(true);
  };

  const handleEditPost = async (postId: number, restorePage: number = 0) => {
    if (await validateAuthentication()) {
      navigate(`/write/${postId}`, { state: { restorePage } });
      return;
    }

    setShowLogin(true);
  };

  const handleDeletePost = async () => {
    if (!postToDelete || postDeleting) {
      return;
    }

    const token = await getValidAccessTokenOrRefresh();
    if (!token) {
      setPostToDelete(null);
      setShowLogin(true);
      return;
    }

    setPostDeleting(true);
    try {
      await deletePost(postToDelete.id, token);
      setPostToDelete(null);
      setSelectedNotice(null);
      await fetchActiveNotices();
      await fetchInitialData(currentPage);
      if (location.pathname.startsWith('/posts/')) {
        navigate('/', { replace: true, state: { refreshPosts: true, restorePage: currentPage } });
      }
    } catch (err) {
      console.error('Failed to delete post', err);
      alert('게시글 삭제에 실패했습니다.');
    } finally {
      setPostDeleting(false);
    }
  };

  const handlePageChange = async (page: number) => {
    if (page < 0 || page >= totalPages || page === currentPage || isFiltered) {
      return;
    }

    await fetchInitialData(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pageGroupStart = Math.floor(currentPage / PAGE_GROUP_SIZE) * PAGE_GROUP_SIZE;
  const pageGroupEnd = Math.min(pageGroupStart + PAGE_GROUP_SIZE, totalPages);
  const pageItems = Array.from({ length: pageGroupEnd - pageGroupStart }, (_, index) => pageGroupStart + index);
  const childCategoriesByParent = categories.reduce((map, category) => {
    if (category.parentId) {
      const children = map.get(category.parentId) || [];
      children.push(category);
      map.set(category.parentId, children);
    }
    return map;
  }, new Map<number, Category[]>());
  const rootCategories = categories.filter((category) => !category.parentId);
  const archiveDayMap = new Map(archiveDays.map((day) => [day.date, day.postCount]));
  const calendarHolidayMap = new Map(calendarHolidays.map((holiday) => [holiday.date, holiday.name]));
  const calendarCells = buildCalendarCells(archiveMonth);
  const todayKey = formatDateKey(new Date());
  const seasonBanner = getCurrentSeasonBanner();
  const mobileHeaderTitle = '보리네';
  const renderMobileCategoryList = () => (
    categoryLoading ? (
      <div className="sidebar-empty">Categories loading...</div>
    ) : rootCategories.length > 0 ? (
      <ListGroup variant="flush">
        {rootCategories.map(category => {
          const children = childCategoriesByParent.get(category.id) || [];
          const isExpanded = expandedCategoryIds.has(category.id) || children.some((child) => child.id === selectedCategoryId);
          return (
            <React.Fragment key={category.id}>
              <ListGroup.Item
                as="div"
                action
                active={selectedCategoryId === category.id}
                className="category-tree-item category-tree-root"
                onClick={() => {
                  handleCategoryClick(category.id);
                  setShowMobileMenu(false);
                }}
              >
                <span className="category-name">{category.name}</span>
                <span className="category-count">{category.postCount}</span>
                {children.length > 0 && (
                  <button
                    type="button"
                    className="category-expand-button"
                    aria-label={`${category.name} child categories ${isExpanded ? 'close' : 'open'}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleCategoryExpanded(category.id);
                    }}
                  >
                    {isExpanded ? '-' : '+'}
                  </button>
                )}
              </ListGroup.Item>
              {isExpanded && children.map((child) => (
                <ListGroup.Item
                  as="div"
                  key={child.id}
                  action
                  active={selectedCategoryId === child.id}
                  className="category-tree-item category-tree-child"
                  onClick={() => {
                    handleCategoryClick(child.id);
                    setShowMobileMenu(false);
                  }}
                >
                  <span className="category-name">{child.name}</span>
                  <span className="category-count">{child.postCount}</span>
                </ListGroup.Item>
              ))}
            </React.Fragment>
          );
        })}
      </ListGroup>
    ) : (
      <div className="sidebar-empty">No categories.</div>
    )
  );

  const renderMobileTagCloud = () => (
    tagLoading ? (
      <div className="sidebar-empty">Tags loading...</div>
    ) : popularTags.length > 0 ? (
      <div className="tag-cloud">
        {popularTags.map((tag) => (
          <button
            key={tag.id}
            type="button"
            className="tag-cloud-item"
            onClick={() => {
              handleTagClick(tag.name);
              setShowMobileMenu(false);
            }}
          >
            <span>{tag.name}</span>
          </button>
        ))}
      </div>
    ) : (
      <div className="sidebar-empty">No tags.</div>
    )
  );

  const renderPopularPosts = (closeMobileMenu = false) => (
    popularPostLoading ? (
      <div className="sidebar-empty">인기 게시물을 불러오는 중입니다.</div>
    ) : popularPosts.length > 0 ? (
      <div className="popular-post-list">
        {popularPosts.map((post) => (
          <Link
            key={post.id}
            to={`/posts/${post.id}`}
            className="popular-post-link"
            onClick={() => {
              if (closeMobileMenu) {
                setShowMobileMenu(false);
              }
            }}
          >
            {post.title}
          </Link>
        ))}
      </div>
    ) : (
      <div className="sidebar-empty">인기 게시물이 없습니다.</div>
    )
  );

  const renderMobileCalendar = () => (
    archiveLoading ? (
      <div className="sidebar-empty">Calendar loading...</div>
    ) : (
      <div className="archive-calendar">
        {WEEKDAY_LABELS.map((label) => (
          <span key={label} className="archive-weekday">{label}</span>
        ))}
        {calendarCells.map((date, index) => {
          if (!date) {
            return <span key={`mobile-empty-${index}`} className="archive-day archive-day-empty" />;
          }

          const dateKey = formatDateKey(date);
          const postCount = archiveDayMap.get(dateKey) ?? 0;
          const hasPosts = postCount > 0;
          const isToday = dateKey === todayKey;
          const holidayName = calendarHolidayMap.get(dateKey);
          const isSunday = date.getDay() === 0;
          const isSaturday = date.getDay() === 6;
          const isHoliday = isSunday || Boolean(holidayName);
          const isDoubleDigit = date.getDate() >= 10;

          return (
            <button
              key={`mobile-${dateKey}`}
              type="button"
              className={`archive-day ${hasPosts ? 'has-posts' : ''} ${isDoubleDigit ? 'double-digit' : ''} ${isSaturday ? 'saturday' : ''} ${isHoliday ? 'holiday' : ''} ${isToday ? 'today' : ''}`}
              disabled={!hasPosts}
              onClick={() => {
                handleArchiveDateClick(dateKey);
                setShowMobileMenu(false);
              }}
            >
              <span>{date.getDate()}</span>
            </button>
          );
        })}
      </div>
    )
  );

  return (
    <Container className="main-container">
      <Row className="header-row">
        <Col xs={12} className="p-0">
          <header className="site-header">
            <button
              type="button"
              className="mobile-header-button mobile-menu-toggle"
              aria-label="모바일 메뉴 열기"
              onClick={() => setShowMobileMenu(true)}
            >
              <i className="bi bi-list" aria-hidden="true" />
            </button>
            <Link
              to="/"
              className="brand-link"
              onClick={() => { setSearchKeyword(''); setSelectedCategoryId(undefined); fetchInitialData(0); }}
            >
              <span className="brand-kicker">Open Personal Blog</span>
              <h1>보리네</h1>
            </Link>
            <div className="season-banner" aria-label={seasonBanner.label}>
              <img src={seasonBanner.image} alt="" aria-hidden="true" />
            </div>
            <div className="mobile-header-title" title={mobileHeaderTitle}>{mobileHeaderTitle}</div>
            <button
              type="button"
              className="mobile-header-button mobile-search-toggle"
              aria-label="검색 열기"
              onClick={() => setMobileSearchOpen((open) => !open)}
            >
              <i className="bi bi-search" aria-hidden="true" />
            </button>
            <div className="header-actions">
              {isAuthenticated ? (
                <>
                  <Button variant="dark" className="me-2" onClick={handleWriteClick}>글쓰기</Button>
                  <Button variant="outline-secondary" onClick={handleLogout}>로그아웃</Button>
                </>
              ) : (
                <Button variant="dark" onClick={() => setShowLogin(true)}>로그인</Button>
              )}
            </div>
            <form
              className={`mobile-search-panel ${mobileSearchOpen ? 'is-open' : ''}`}
              onSubmit={(event) => {
                handleSearch(event);
                setMobileSearchOpen(false);
              }}
            >
              <input
                ref={mobileSearchInputRef}
                type="search"
                className="form-control"
                placeholder="검색어를 입력하세요"
                value={searchKeyword}
                onChange={(event) => setSearchKeyword(event.target.value)}
                aria-label="검색어"
                tabIndex={mobileSearchOpen ? 0 : -1}
              />
            </form>
          </header>
          <Offcanvas show={showMobileMenu} onHide={() => setShowMobileMenu(false)} className="mobile-nav-drawer">
            <Offcanvas.Header closeButton>
              <Offcanvas.Title>
                <span className="mobile-menu-brand">
                  <img src="/images/profile-cover.jpg" alt="" aria-hidden="true" />
                  <span>보리네</span>
                </span>
              </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
              <section className="mobile-drawer-section">
                <div className="mobile-drawer-section-head">
                  <h5 className="sidebar-header">Categories</h5>
                  <button type="button" className="mobile-show-all-button" onClick={handleShowAllPosts}>
                    전체
                  </button>
                </div>
                {renderMobileCategoryList()}
              </section>
              <section className="mobile-drawer-section">
                <h5 className="sidebar-header">Tags</h5>
                {renderMobileTagCloud()}
              </section>
              <section className="mobile-drawer-section">
                <h5 className="sidebar-header">Popular Posts</h5>
                {renderPopularPosts(true)}
              </section>
              <section className="mobile-drawer-section">
                <div className="archive-calendar-head">
                  <h5 className="sidebar-header">Calendar</h5>
                  <div className="archive-calendar-nav">
                    <button
                      type="button"
                      aria-label="이전 달"
                      onClick={() => setArchiveMonth(new Date(archiveMonth.getFullYear(), archiveMonth.getMonth() - 1, 1))}
                    >
                      &lt;
                    </button>
                    <strong>{getMonthLabel(archiveMonth)}</strong>
                    <button
                      type="button"
                      aria-label="다음 달"
                      onClick={() => setArchiveMonth(new Date(archiveMonth.getFullYear(), archiveMonth.getMonth() + 1, 1))}
                    >
                      &gt;
                    </button>
                  </div>
                </div>
                {renderMobileCalendar()}
              </section>
              <section className="mobile-drawer-section mobile-drawer-actions">
                {isAuthenticated ? (
                  <>
                    <Button variant="dark" onClick={async () => { await handleWriteClick(); setShowMobileMenu(false); }}>글쓰기</Button>
                    <Button variant="outline-secondary" onClick={() => { setShowMobileMenu(false); handleLogout(); }}>로그아웃</Button>
                  </>
                ) : (
                  <Button variant="dark" onClick={() => { setShowMobileMenu(false); setShowLogin(true); }}>로그인</Button>
                )}
              </section>
              <section className="mobile-drawer-section">
                <h5 className="sidebar-header">Visitors</h5>
                {visitorLoading ? (
                  <div className="sidebar-empty">방문자 정보를 불러오는 중입니다.</div>
                ) : visitorStats ? (
                  <div className="visitor-counts">
                    <div>
                      <span>Today</span>
                      <strong>{visitorStats.today.toLocaleString()}</strong>
                    </div>
                    <div>
                      <span>Total</span>
                      <strong>{visitorStats.total.toLocaleString()}</strong>
                    </div>
                  </div>
                ) : (
                  <div className="sidebar-empty">방문자 정보가 없습니다.</div>
                )}
              </section>
              <section className="mobile-drawer-section">
                <div className="weather-head">
                  <h5 className="sidebar-header">Weather</h5>
                  <select
                    className="weather-region-select"
                    aria-label="날씨 지역 선택"
                    value={selectedWeatherRegion.key}
                    onChange={(e) => {
                      const nextRegion = WEATHER_REGIONS.find((region) => region.key === e.target.value);
                      if (nextRegion) {
                        setSelectedWeatherRegion(nextRegion);
                      }
                    }}
                  >
                    {WEATHER_REGIONS.map((region) => (
                      <option key={region.key} value={region.key}>{region.name}</option>
                    ))}
                  </select>
                </div>
                {weatherLoading ? (
                  <div className="sidebar-empty">날씨를 불러오는 중입니다.</div>
                ) : weatherError || !weather ? (
                  <div className="sidebar-empty">{weatherError || '날씨 정보가 없습니다.'}</div>
                ) : (
                  <>
                    <div className="weather-current">
                      <span className="weather-location">{selectedWeatherRegion.name}</span>
                      <strong className="weather-temp">{weather.current.temperature}</strong>
                      <span className="weather-condition">{weather.current.precipitationType}</span>
                    </div>
                    <div className="weather-meta">
                      <span>습도 {weather.current.humidity}</span>
                      <span>강수 {weather.current.rainfall}</span>
                      <span>풍속 {weather.current.windSpeed}</span>
                    </div>
                  </>
                )}
              </section>
            </Offcanvas.Body>
          </Offcanvas>
        </Col>
      </Row>
      <Row className={`content-row ${isWriting ? 'writing-layout' : ''}`}>
        <Col lg={3} xl={2} className="left-sidebar">
          <div className="profile-section side-surface">
            <div className="profile-cover-frame">
              <img className="profile-cover" src="/images/profile-cover.jpg" alt="Profile cover" />
            </div>
            <h5 className="sidebar-header">Profile</h5>
            <p className="profile-copy">보리네 삶과 흔적을 차분히 남기는 개인 블로그입니다.</p>
          </div>
          <div className="weather-section side-surface">
            <div className="weather-head">
              <h5 className="sidebar-header">Weather</h5>
              <select
                className="weather-region-select"
                aria-label="날씨 지역 선택"
                value={selectedWeatherRegion.key}
                onChange={(e) => {
                  const nextRegion = WEATHER_REGIONS.find((region) => region.key === e.target.value);
                  if (nextRegion) {
                    setSelectedWeatherRegion(nextRegion);
                  }
                }}
              >
                {WEATHER_REGIONS.map((region) => (
                  <option key={region.key} value={region.key}>{region.name}</option>
                ))}
              </select>
            </div>
            {weatherLoading ? (
              <div className="sidebar-empty">날씨를 불러오는 중입니다.</div>
            ) : weatherError || !weather ? (
              <div className="sidebar-empty">{weatherError || '날씨 정보가 없습니다.'}</div>
            ) : (
              <>
                <div className="weather-current">
                  <span className="weather-location">{selectedWeatherRegion.name}</span>
                  <strong className="weather-temp">{weather.current.temperature}</strong>
                  <span className="weather-condition">
                    {weather.current.precipitationType === '없음' ? '강수없음' : weather.current.precipitationType}
                  </span>
                </div>
                <div className="weather-meta">
                  <span>습도 {weather.current.humidity}</span>
                  <span>강수 {weather.current.rainfall}</span>
                  <span>풍속 {weather.current.windSpeed}</span>
                </div>
                <div className="weather-forecast">
                  {weather.forecasts.map((forecast) => (
                    <div className="weather-row" key={`${forecast.date}-${forecast.time}`}>
                      <span>{forecast.time}</span>
                      <strong>{forecast.temperature}</strong>
                      <span>{forecast.precipitationType === '없음' ? forecast.sky : forecast.precipitationType}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          <div className="visitor-section side-surface">
            <h5 className="sidebar-header">Visitors</h5>
            {visitorLoading ? (
              <div className="sidebar-empty">방문자 정보를 불러오는 중입니다.</div>
            ) : visitorStats ? (
              <>
                <div className="visitor-counts">
                  <div>
                    <span>Today</span>
                    <strong>{visitorStats.today.toLocaleString()}</strong>
                  </div>
                  <div>
                    <span>Total</span>
                    <strong>{visitorStats.total.toLocaleString()}</strong>
                  </div>
                </div>
                <div className="visitor-global">
                  <span>Global</span>
                  <strong>{visitorStats.overseasRate}%</strong>
                </div>
              </>
            ) : (
              <div className="sidebar-empty">방문자 정보가 없습니다.</div>
            )}
          </div>
        </Col>
        <Col lg={isWriting ? 9 : 6} xl={isWriting ? 10 : 8} className="center-content">
          <main>
            <Routes>
              <Route path="/" element={
                <div className="post-list home-post-list">
                  {activeNotices.length > 0 && (
                    <section className="notice-board">
                      <div className="notice-board-head">
                        <strong>공지사항</strong>
                      </div>
                      <div className="notice-board-list">
                        {activeNotices.map((notice) => (
                          <div key={notice.id} className="notice-board-row">
                            <Link
                              to={`/posts/${notice.id}`}
                              className="notice-board-title-button"
                            >
                              <span className="notice-board-label">[공지사항]</span>
                              <span className="notice-board-title">{notice.title}</span>
                            </Link>
                            {isAuthenticated && (
                              <div className="notice-board-actions">
                                <button type="button" aria-label="공지사항 수정" onClick={() => handleEditPost(notice.id)}>
                                  <i className="bi bi-pencil-square" />
                                </button>
                                <button type="button" aria-label="공지사항 삭제" onClick={() => setPostToDelete(notice)}>
                                  <i className="bi bi-trash3" />
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </section>
                  )}
                  {loading ? (
                    <div className="empty-state">게시물을 불러오는 중입니다.</div>
                  ) : posts.length > 0 ? (
                    <>
                      {posts.map(post => (
                        <PostItem
                          key={post.id}
                          post={post}
                          onTagClick={handleTagClick}
                          isAuthenticated={isAuthenticated}
                          currentUserNickname={currentUser?.nickname || undefined}
                          onEdit={(postId) => handleEditPost(postId, currentPage)}
                          onDelete={setPostToDelete}
                        />
                      ))}
                      {totalPages > 1 && !isFiltered && (
                        <Pagination className="blog-pagination">
                          <Pagination.First disabled={currentPage === 0} onClick={() => handlePageChange(Math.max(currentPage - PAGE_GROUP_SIZE, 0))}>
                            &lt;&lt;
                          </Pagination.First>
                          <Pagination.Prev disabled={currentPage === 0} onClick={() => handlePageChange(currentPage - 1)}>
                            &lt;
                          </Pagination.Prev>
                          {pageItems.map((page) => (
                            <Pagination.Item key={page} active={page === currentPage} onClick={() => handlePageChange(page)}>
                              {page + 1}
                            </Pagination.Item>
                          ))}
                          <Pagination.Next disabled={currentPage >= totalPages - 1} onClick={() => handlePageChange(currentPage + 1)}>
                            &gt;
                          </Pagination.Next>
                          <Pagination.Last disabled={currentPage >= totalPages - 1} onClick={() => handlePageChange(Math.min(currentPage + PAGE_GROUP_SIZE, totalPages - 1))}>
                            &gt;&gt;
                          </Pagination.Last>
                        </Pagination>
                      )}
                    </>
                  ) : (
                    <div className="empty-state">등록된 게시물이 없습니다.</div>
                  )}
                </div>
              } />
              <Route path="/posts/:postId" element={
                <PostDetailPage
                  isAuthenticated={isAuthenticated}
                  currentUserNickname={currentUser?.nickname || undefined}
                  onEdit={(postId) => handleEditPost(postId, currentPage)}
                  onDelete={setPostToDelete}
                  onRequireLogin={() => setShowLogin(true)}
                />
              } />
              <Route path="/write" element={<EditorPage />} />
              <Route path="/write/:postId" element={<EditorPage />} />
            </Routes>
          </main>
        </Col>
        {!isWriting && (
          <Col lg={3} xl={2} className="right-sidebar">
            <div className="search-section side-surface">
              <h5 className="sidebar-header">Search</h5>
              <form onSubmit={handleSearch}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="제목과 본문 검색"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                />
              </form>
            </div>
            <div className="category-section side-surface">
              <h5 className="sidebar-header">Categories</h5>
              {categoryLoading ? (
                <div className="sidebar-empty">카테고리를 불러오는 중입니다.</div>
              ) : rootCategories.length > 0 ? (
                <ListGroup variant="flush">
                  {rootCategories.map(category => {
                    const children = childCategoriesByParent.get(category.id) || [];
                    const isExpanded = expandedCategoryIds.has(category.id) || children.some((child) => child.id === selectedCategoryId);
                    return (
                      <React.Fragment key={category.id}>
                        <ListGroup.Item
                          as="div"
                          action
                          active={selectedCategoryId === category.id}
                          className="category-tree-item category-tree-root"
                          onClick={() => handleCategoryClick(category.id)}
                        >
                          <span className="category-name">{category.name}</span>
                          <span className="category-count">{category.postCount}</span>
                          {children.length > 0 && (
                            <button
                              type="button"
                              className="category-expand-button"
                              aria-label={`${category.name} 하위 카테고리 ${isExpanded ? '접기' : '펼치기'}`}
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleCategoryExpanded(category.id);
                              }}
                            >
                              {isExpanded ? '−' : '+'}
                            </button>
                          )}
                        </ListGroup.Item>
                        {isExpanded && children.map((child) => (
                          <ListGroup.Item
                            as="div"
                            key={child.id}
                            action
                            active={selectedCategoryId === child.id}
                            className="category-tree-item category-tree-child"
                            onClick={() => handleCategoryClick(child.id)}
                          >
                            <span className="category-name">{child.name}</span>
                            <span className="category-count">{child.postCount}</span>
                          </ListGroup.Item>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </ListGroup>
              ) : (
                <div className="sidebar-empty">등록된 카테고리가 없습니다.</div>
              )}
            </div>
            <div className="tag-cloud-section side-surface">
              <h5 className="sidebar-header">Tags</h5>
              {tagLoading ? (
                <div className="sidebar-empty">태그를 불러오는 중입니다.</div>
              ) : popularTags.length > 0 ? (
                <div className="tag-cloud">
                  {popularTags.map((tag) => (
                    <button
                      key={tag.id}
                      type="button"
                      className="tag-cloud-item"
                      onClick={() => handleTagClick(tag.name)}
                    >
                      <span>{tag.name}</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="sidebar-empty">등록된 태그가 없습니다.</div>
              )}
            </div>
            <div className="popular-post-section side-surface">
              <h5 className="sidebar-header">Popular Posts</h5>
              {renderPopularPosts()}
            </div>
            <div className="archive-calendar-section side-surface">
              <div className="archive-calendar-head">
                <h5 className="sidebar-header">Calendar</h5>
                <div className="archive-calendar-nav">
                  <button
                    type="button"
                    aria-label="이전 달"
                    onClick={() => setArchiveMonth(new Date(archiveMonth.getFullYear(), archiveMonth.getMonth() - 1, 1))}
                  >
                    &lt;
                  </button>
                  <strong>{getMonthLabel(archiveMonth)}</strong>
                  <button
                    type="button"
                    aria-label="다음 달"
                    onClick={() => setArchiveMonth(new Date(archiveMonth.getFullYear(), archiveMonth.getMonth() + 1, 1))}
                  >
                    &gt;
                  </button>
                </div>
              </div>
              {archiveLoading ? (
                <div className="sidebar-empty">달력을 불러오는 중입니다.</div>
              ) : (
                <div className="archive-calendar">
                  {WEEKDAY_LABELS.map((label) => (
                    <span key={label} className="archive-weekday">{label}</span>
                  ))}
                  {calendarCells.map((date, index) => {
                    if (!date) {
                      return <span key={`empty-${index}`} className="archive-day archive-day-empty" />;
                    }

                    const dateKey = formatDateKey(date);
                    const postCount = archiveDayMap.get(dateKey) ?? 0;
                    const hasPosts = postCount > 0;
                    const isToday = dateKey === todayKey;
                    const holidayName = calendarHolidayMap.get(dateKey);
                    const isSunday = date.getDay() === 0;
                    const isSaturday = date.getDay() === 6;
                    const isHoliday = isSunday || Boolean(holidayName);
                    const isDoubleDigit = date.getDate() >= 10;
                    const dayTitle = [
                      dateKey,
                      holidayName || (isSunday ? '일요일' : isSaturday ? '토요일' : ''),
                      hasPosts ? `게시글 ${postCount}건` : '',
                    ].filter(Boolean).join(' · ');

                    return (
                      <button
                        key={dateKey}
                        type="button"
                        className={`archive-day ${hasPosts ? 'has-posts' : ''} ${isDoubleDigit ? 'double-digit' : ''} ${isSaturday ? 'saturday' : ''} ${isHoliday ? 'holiday' : ''} ${isToday ? 'today' : ''}`}
                        disabled={!hasPosts}
                        onClick={() => handleArchiveDateClick(dateKey)}
                        title={dayTitle}
                      >
                        <span>{date.getDate()}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </Col>
        )}
      </Row>
      <footer className="site-footer">
        <div className="footer-brand">
          <strong>Borine&apos;s Story</strong>
          <span>보리네 이야기</span>
        </div>
        <div className="footer-info">
          <span>CONTACT : <a href="mailto:boripapa@outlook.kr">boripapa@outlook.kr</a></span>
          <span>COPYRIGHT © 보리네 이야기. ALL RIGHTS RESERVED.</span>
        </div>
      </footer>
      <Modal
        show={!!selectedNotice}
        onHide={() => setSelectedNotice(null)}
        centered
        dialogClassName="notice-modal-dialog"
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedNotice?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div
            className="notice-modal-content post-content"
            dangerouslySetInnerHTML={{ __html: selectedNotice?.content || '' }}
          />
        </Modal.Body>
      </Modal>
      <Modal show={!!postToDelete} onHide={() => setPostToDelete(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{postToDelete?.category?.name === '공지사항' ? '공지사항 삭제' : '게시글 삭제'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {postToDelete?.category?.name === '공지사항' ? '공지사항을 삭제하시겠습니까?' : '게시글을 삭제하시겠습니까?'}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setPostToDelete(null)} disabled={postDeleting}>
            취소
          </Button>
          <Button variant="danger" onClick={handleDeletePost} disabled={postDeleting}>
            {postDeleting ? '삭제 중...' : '삭제'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

const App: React.FC = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);

  const validateAuthentication = useCallback(async () => {
    const token = await getValidAccessTokenOrRefresh();
    const isValid = !!token;
    setIsAuthenticated(isValid);
    if (!token) {
      setCurrentUser(null);
      return false;
    }

    try {
      setCurrentUser(await getCurrentUser(token));
    } catch (error) {
      clearAuthCookies();
      setIsAuthenticated(false);
      setCurrentUser(null);
      return false;
    }
    return isValid;
  }, []);

  useEffect(() => {
    setIsAuthenticated(hasValidAuthToken());
    validateAuthentication();
    const handleFocus = () => {
      validateAuthentication();
    };

    const intervalId = window.setInterval(() => {
      validateAuthentication();
    }, 60 * 1000);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, [validateAuthentication]);

  const handleLoginSuccess = () => {
    validateAuthentication();
  };

  const handleLogout = () => {
    clearAuthCookies();
    setIsAuthenticated(false);
    setCurrentUser(null);
  };

  return (
    <>
      <MainLayout 
        key={isAuthenticated ? 'authenticated' : 'guest'}
        isAuthenticated={isAuthenticated} 
        currentUser={currentUser}
        handleLogout={handleLogout} 
        setShowLogin={setShowLogin} 
        validateAuthentication={validateAuthentication}
      />
      <LoginModal 
        show={showLogin} 
        onHide={() => setShowLogin(false)} 
        onLoginSuccess={handleLoginSuccess} 
      />
    </>
  );
};

export default App;
